import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {AccordionDetails, CircularProgress, styled, Typography} from '@mui/material';
import MuiAccordion, {AccordionProps} from '@mui/material/Accordion';
import MuiAccordionSummary, {AccordionSummaryProps} from '@mui/material/AccordionSummary';
import {forwardRef, ReactNode, SyntheticEvent, useImperativeHandle} from 'react';
import {useClickOutside} from '../../../hooks/useClickOutside';

interface Props extends Pick<AccordionProps, 'onChange' | 'children' | 'disabled'> {
  summaryIcon?: ReactNode;
  summaryContent: ReactNode;
  isLoading?: boolean;
}

interface StyledAccordionProps extends AccordionProps {
  panelRef: React.RefObject<HTMLDivElement>;
}

export interface AccordionHandle {
  close: () => void;
}

const Accordion = styled(({panelRef, ...props}: StyledAccordionProps) => (
  <MuiAccordion ref={panelRef} disableGutters elevation={0} square {...props} />
))(({theme}) => ({
  border: `1px solid ${theme.palette.divider}`,
  width: '300px',
  '&.Mui-disabled': {backgroundColor: '#f3f3f3 !important'},
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => <MuiAccordionSummary {...props} />)(() => ({
  '& .MuiAccordionSummary-content': {
    margin: 0,
  },
  minHeight: 32,
}));

export const FiltersAccordion = forwardRef<AccordionHandle, Props>(function FiltersAccordionInner(
  {summaryContent, summaryIcon, isLoading, children, onChange, ...props},
  ref,
) {
  const {isComponentVisible, ref: panelRef, setIsComponentVisible} = useClickOutside(false);

  const handleAccordionChange = (e: SyntheticEvent, expand: boolean) => {
    setIsComponentVisible(expand);
    onChange?.(e, expand);
  };

  useImperativeHandle(
    ref,
    () => ({
      isVisible: isComponentVisible,
      close: () => setIsComponentVisible(false),
    }),
    [isComponentVisible, setIsComponentVisible],
  );

  return (
    <Accordion panelRef={panelRef} onChange={handleAccordionChange} expanded={isComponentVisible} {...props}>
      <AccordionSummary expandIcon={isLoading ? <CircularProgress size={20} /> : <ExpandMoreIcon />}>
        {summaryIcon}
        <Typography variant="subtitle2" fontWeight="bold">
          {summaryContent}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
});
